import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { requiresHistoricalMigration, MIGRATION_PREREQUISITES } from "@/lib/historicalMigration";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, ChevronLeft, ChevronRight, UserCheck } from "lucide-react";

const STEPS = ["Customer", "Product", "Profile", "Stage", "Automation", "Review"];

const DEDUP_LABEL = {
  new: null,
  existing: "Existing customer found",
  possible_duplicate: "Possible existing match — review before continuing",
};

const emptyForm = () => ({
  name: "", phone: "", whatsappSame: true, whatsapp: "", email: "", city: "",
  source: "manual",
});

/* 2026-09 Phase 3A — the ONE canonical onboarding flow for every "Create
   customer / New Lead" entry point in SalesOS (Customers, Calls, Leads,
   Finance, Process). Steps: Customer details -> Product -> Product-
   specific profile -> Starting stage -> Automation decision -> Review &
   Create. Never creates the contact/opportunity until the final Create —
   dedup-check (Step 1 -> 2) is the one read-only exception, reusing
   services.resolve_contact_identity in dry_run mode so nothing is written
   just to check whether a customer already exists. */
export const OnboardingWizardDialog = ({ onCreated, triggerLabel = "New lead", triggerTestId = "onboarding-wizard-button" }) => {
  const { hasCap, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");

  // Step 1 — customer details + dedup
  const [form, setForm] = useState(emptyForm());
  const [dedupChecking, setDedupChecking] = useState(false);
  const [dedupResult, setDedupResult] = useState(null); // {match, contact, opportunities}

  // Step 2 — product
  const [products, setProducts] = useState([]);
  const [productCode, setProductCode] = useState("");

  // Step 3 — product-specific profile
  const [fields, setFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});

  // Step 4 — starting stage
  const [stages, setStages] = useState([]);
  const [advancedStage, setAdvancedStage] = useState(false);
  const [stageId, setStageId] = useState("");
  const [stageReason, setStageReason] = useState("");
  // R13 Workstream 6 - explicit, permission-controlled TEST classification (never inferred from a name).
  const [isTest, setIsTest] = useState(false);

  // Step 5 — automation
  const [automationPreview, setAutomationPreview] = useState(null);
  const [startAutomation, setStartAutomation] = useState(false);

  const canOverrideStage = hasCap("stage.override");

  const reset = () => {
    setStep(0); setForm(emptyForm()); setDedupResult(null); setDedupChecking(false);
    setProducts([]); setProductCode(""); setFields([]); setFieldValues({});
    setStages([]); setAdvancedStage(false); setStageId(""); setStageReason(""); setIsTest(false);
    setAutomationPreview(null); setStartAutomation(false); setBusy(false);
    setIdempotencyKey((typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID() : `wiz-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  };

  useEffect(() => { if (open) reset(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step === 1) api.get("/products?selectable_only=true").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [step]);

  useEffect(() => {
    if (step !== 2 || !productCode) return;
    api.get(`/custom-fields?product_code=${productCode}`).then((r) => setFields(r.data)).catch(() => setFields([]));
  }, [step, productCode]);

  useEffect(() => {
    if (step !== 3 || !productCode) return;
    api.get(`/opportunities/pipeline-stages?product_code=${productCode}`).then((r) => {
      setStages(r.data);
      if (r.data[0]) setStageId(r.data[0].stage_id);
    }).catch(() => setStages([]));
  }, [step, productCode]);

  useEffect(() => {
    if (step !== 4 || !productCode) return;
    api.get(`/leads/automation-preview?product_code=${productCode}`).then((r) => setAutomationPreview(r.data)).catch(() => setAutomationPreview(null));
  }, [step, productCode]);

  const runDedupCheck = async () => {
    if (!form.name.trim() || (!form.phone.trim() && !form.email.trim())) {
      toast.warning("Name, and phone or email, are required");
      return;
    }
    setDedupChecking(true);
    try {
      const { data } = await api.post("/leads/dedup-check", {
        name: form.name, phone: form.phone || null, email: form.email || null,
      });
      setDedupResult(data);
      setStep(1);
    } catch (e) { toast.error(errDetail(e)); } finally { setDedupChecking(false); }
  };

  const defaultStage = stages[0];
  const stageIsDefault = !stageId || !defaultStage || stageId === defaultStage.stage_id;
  // R13 Workstream 5 - placing a customer directly in a PROCESS stage is a historical migration, not an
  // ordinary advanced stage choice: it needs evidence, an attestation and owners.
  const selectedStage = stages.find((s) => s.stage_id === stageId);
  const needsMigration = requiresHistoricalMigration({ advancedStage, stages, stageId });

  const create = async () => {
    if (needsMigration) { toast.error("Use governed historical migration to place a customer in a Process stage."); return; }
    setBusy(true);
    const custom_fields = {};
    for (const f of fields) {
      const v = fieldValues[f.key];
      if (v !== undefined && v !== null && v !== "") custom_fields[f.key] = v;
    }
    try {
      const { data } = await api.post("/leads", {
        name: form.name, phone: form.phone || null,
        whatsapp_phone: form.whatsappSame ? null : (form.whatsapp || null),
        email: form.email || null, city: form.city || null,
        product_code: productCode, source: form.source || "manual",
        is_test_record: isTest,
        starting_stage_id: stageIsDefault ? null : stageId,
        stage_override_reason: stageIsDefault ? null : stageReason,
        start_automation: startAutomation,
        custom_fields, creation_source: "manual_wizard",
        external_event_id: idempotencyKey,
      });
      const d = DEDUP_LABEL[dedupResult?.match] || null;
      toast.success(d ? `Created — ${d}` : "Customer created");
      setOpen(false);
      onCreated && onCreated(data);
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  const next = () => {
    if (step === 0) { runDedupCheck(); return; }
    if (step === 1) { if (!productCode) { toast.warning("Pick a product"); return; } setStep(2); return; }
    if (step === 2) {
      const missing = fields.filter((f) => f.required && !fieldValues[f.key]);
      if (missing.length) { toast.warning(`Required: ${missing.map((f) => f.label).join(", ")}`); return; }
      setStep(3); return;
    }
    if (step === 3) {
      if (needsMigration) {
        toast.warning("A Process stage needs governed historical migration - it asks for the evidence a Case requires.");
        return;
      }
      if (advancedStage && !stageIsDefault && !stageReason.trim()) {
        toast.warning("A reason is required to place this opportunity on a stage other than Lead Creation");
        return;
      }
      setStep(4); return;
    }
    if (step === 4) { setStep(5); return; }
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const fieldInput = (f) => {
    const v = fieldValues[f.key] ?? "";
    const set = (val) => setFieldValues({ ...fieldValues, [f.key]: val });
    if (f.type === "boolean") {
      return (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={!!v} onCheckedChange={(c) => set(!!c)} /> {f.label}
        </label>
      );
    }
    if (f.type === "dropdown") {
      return (
        <Select value={v || undefined} onValueChange={set}>
          <SelectTrigger className="h-9"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    if (f.type === "longtext") {
      return <Textarea rows={2} value={v} onChange={(e) => set(e.target.value)} />;
    }
    if (f.type === "number" || f.type === "currency") {
      return <Input type="number" value={v} onChange={(e) => set(e.target.value)} />;
    }
    if (f.type === "date") {
      return <Input type="date" value={v} onChange={(e) => set(e.target.value)} />;
    }
    return <Input value={v} onChange={(e) => set(e.target.value)} />;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={triggerTestId}>
          <Plus className="h-4 w-4 mr-1.5" /> {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="onboarding-wizard">
        <DialogHeader>
          <DialogTitle className="font-heading">{triggerLabel}</DialogTitle>
          <DialogDescription>
            Step {step + 1} of {STEPS.length} — {STEPS[step]}
          </DialogDescription>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Full name *</Label>
              <Input data-testid="wiz-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Rohan Gupta" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Primary phone</Label>
                <Input data-testid="wiz-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="98765 43210" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input data-testid="wiz-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@email.com" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox data-testid="wiz-whatsapp-same" checked={form.whatsappSame}
                onCheckedChange={(c) => setForm({ ...form, whatsappSame: !!c })} />
              WhatsApp number is the same as primary phone
            </label>
            {!form.whatsappSame && (
              <div className="space-y-1.5">
                <Label>WhatsApp number</Label>
                <Input data-testid="wiz-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>City / location</Label>
                <Input data-testid="wiz-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Source</Label>
                <Input data-testid="wiz-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            {dedupResult?.match && dedupResult.match !== "new" && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm space-y-1" data-testid="wiz-dedup-match">
                <div className="flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400">
                  <UserCheck className="h-4 w-4" /> {DEDUP_LABEL[dedupResult.match]}
                </div>
                <p><b>{dedupResult.contact?.full_name}</b> · {dedupResult.contact?.customer_id} · {dedupResult.contact?.primary_phone || dedupResult.contact?.email}</p>
                {(dedupResult.opportunities || []).length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Existing products: {dedupResult.opportunities.map((o) => o.product_name || o.product_code).join(", ")}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Continuing will add a new product/opportunity to this existing customer — their existing contact details are never changed by this wizard.
                </p>
              </div>
            )}
            {(!dedupResult?.match || dedupResult.match === "new") && (
              <p className="text-sm text-muted-foreground" data-testid="wiz-dedup-new">No existing customer matched — this will create a new customer.</p>
            )}
            <div className="space-y-1.5">
              <Label>Product *</Label>
              <Select value={productCode} onValueChange={setProductCode}>
                <SelectTrigger data-testid="wiz-product-select" className="h-9"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            {fields.length === 0 ? (
              <p className="text-sm text-muted-foreground">No product-specific profile fields configured for this product yet.</p>
            ) : fields.map((f) => (
              <div key={f.key} className="space-y-1.5" data-testid={`wiz-field-${f.key}`}>
                <Label className="flex items-center gap-1.5">
                  {f.label}
                  {f.required ? <Badge variant="outline" className="text-[9px]">required</Badge>
                    : <Badge variant="outline" className="text-[9px] text-muted-foreground">optional</Badge>}
                </Label>
                {fieldInput(f)}
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">
              Fields left blank are simply not recorded — the opportunity can still be created; missing information will show clearly on Customer 360.
            </p>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div className="rounded-md border p-3 text-sm">
              <p><b>Starting stage:</b> {defaultStage?.name || "Lead Creation"}</p>
              <p className="text-xs text-muted-foreground mt-1">Every new opportunity starts here by default — this is the only stage most employees should ever need.</p>
            </div>
            {canOverrideStage && (
              <>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox data-testid="wiz-advanced-stage" checked={advancedStage}
                    onCheckedChange={(c) => { setAdvancedStage(!!c); if (!c) { setStageId(defaultStage?.stage_id || ""); setStageReason(""); } }} />
                  Advanced: place this opportunity on a different stage
                </label>
                {advancedStage && (
                  <div className="space-y-3 rounded-md border p-3">
                    <p className="text-xs text-muted-foreground">
                      Only use this when the customer genuinely already belongs at a later stage (e.g. a historical/walk-in record). This is audited and requires a reason.
                    </p>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Stage</Label>
                      <Select value={stageId} onValueChange={setStageId}>
                        <SelectTrigger data-testid="wiz-stage-select" className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {stages.map((s) => <SelectItem key={s.stage_id} value={s.stage_id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {needsMigration && (
                      <div data-testid="wiz-migration-required" role="alert"
                        className="rounded border border-amber-500/50 bg-amber-500/10 p-2.5 text-xs space-y-1.5">
                        <p className="font-medium">{selectedStage?.name} is a Process stage - this is a historical migration</p>
                        <p>A free-text reason is not enough, and no operational Case is created until every
                          prerequisite below is present. Create stays disabled here.</p>
                        <ul data-testid="wiz-migration-missing" className="list-disc pl-5 space-y-0.5">
                          {MIGRATION_PREREQUISITES.map((m) => <li key={m.code} data-testid={`wiz-missing-${m.code}`}>{m.label}</li>)}
                        </ul>
                        <Button type="button" size="sm" variant="outline" data-testid="wiz-open-migration"
                          onClick={() => { setOpen(false); navigate("/process?migration=1"); }}>
                          Open governed historical migration
                        </Button>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <Label className="text-xs">Reason (required, audited)</Label>
                      <Textarea data-testid="wiz-stage-reason" rows={2} value={stageReason} onChange={(e) => setStageReason(e.target.value)} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3">
            {automationPreview && !automationPreview.applicable ? (
              <p className="text-sm text-muted-foreground" data-testid="wiz-automation-none">No automatic outreach is configured for this product/stage.</p>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <Label className="text-sm">SalesOS automation</Label>
                </div>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant={!startAutomation ? "default" : "outline"}
                    data-testid="wiz-automation-off" onClick={() => setStartAutomation(false)}>Do not start automation</Button>
                  <Button type="button" size="sm" variant={startAutomation ? "default" : "outline"}
                    data-testid="wiz-automation-on" onClick={() => setStartAutomation(true)}>Start automation</Button>
                </div>
                <div className="rounded-md border p-3 text-sm space-y-1" data-testid="wiz-automation-preview">
                  <p className="font-medium">{startAutomation ? "If you continue, automation ON will:" : "Automation is OFF — none of the following will happen:"}</p>
                  <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-0.5">
                    {(automationPreview?.actions || []).map((a) => <li key={a.key}>{a.label}</li>)}
                  </ul>
                  <p className="text-[11px] text-muted-foreground pt-1">Standard workflow tasks (e.g. Record call outcome) are always created either way — this choice only affects automated outreach.</p>
                </div>
              </>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border p-3 space-y-1">
              <p><b>{form.name}</b> {form.phone && `· ${form.phone}`} {form.email && `· ${form.email}`}</p>
              <p className="text-xs text-muted-foreground">{dedupResult?.match && dedupResult.match !== "new" ? `Adding to existing customer ${dedupResult.contact?.customer_id}` : "New customer"}</p>
              <p>Product: <b>{products.find((p) => p.code === productCode)?.name || productCode}</b></p>
              <p>Starting stage: <b>{stageIsDefault ? (defaultStage?.name || "Lead Creation") : stages.find((s) => s.stage_id === stageId)?.name}</b></p>
            </div>
            {hasCap("admin.config") && (
              <label className="flex items-center gap-2 rounded-md border p-3 text-sm" data-testid="wiz-test-classification">
                <Checkbox checked={isTest} onCheckedChange={(c) => setIsTest(!!c)} />
                <span><b>Classify as TEST data</b> <span className="text-xs text-muted-foreground">
                  (hidden from every operational view and report, never contacts a provider - set deliberately, never inferred from the name)</span></span>
              </label>
            )}
            {isTest && <Badge variant="outline" data-testid="wiz-test-badge">TEST</Badge>}
            <div className={`rounded-md border p-3 ${startAutomation ? "border-emerald-500/40 bg-emerald-500/10" : "border-muted"}`} data-testid="wiz-final-automation-summary">
              <p className="font-medium">{startAutomation ? "Automation ON" : "Automation OFF"}</p>
              <p className="text-xs text-muted-foreground">
                {startAutomation
                  ? "SalesOS will start the configured automated outreach for this product after this customer is created."
                  : "No automatic WhatsApp or AI Voice outreach will start. Employees can start outreach manually later."}
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <Button variant="ghost" size="sm" disabled={step === 0 || busy} onClick={back}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {step < 5 ? (
            <Button data-testid="wiz-next" size="sm" disabled={dedupChecking} onClick={next}>
              {dedupChecking ? "Checking…" : <>Next <ChevronRight className="h-4 w-4 ml-1" /></>}
            </Button>
          ) : (
            <Button data-testid="wiz-create" size="sm" disabled={busy || needsMigration} onClick={create}>
              {busy ? "Creating…" : "Create"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
